import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronsUpDown, Check } from 'lucide-react';
import { NavItem } from '../../../hooks/useNavigation';
import { PortalPopover } from '../../ui/PortalPopover';
import { Button } from '../../ui/Button';

export interface BreadcrumbSubpageDropdownProps {
  activeItem: NavItem;
  items: NavItem[];
  title?: string;
}

export const BreadcrumbSubpageDropdown: React.FC<BreadcrumbSubpageDropdownProps> = ({
  activeItem,
  items,
  title = 'Views',
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const Icon = activeItem.icon;

  return (
    <div className="relative shrink-0">
      <Button
        ref={triggerRef as any}
        type="button"
        variant="ghost"
        size="xs"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-cf-surfaceActive font-medium text-cf-textPrimary transition-colors h-auto"
        title={`Switch ${title}`}
      >
        <Icon size={13} className="text-cf-orange flex-shrink-0" />
        <span className="max-w-[85px] sm:max-w-[140px] truncate">{activeItem.label}</span>
        <ChevronsUpDown size={11} className="text-cf-textMuted flex-shrink-0" />
      </Button>

      <PortalPopover
        isOpen={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
        align="left"
        className="w-56 bg-cf-surface border border-cf-border rounded-lg shadow-xl overflow-hidden text-xs"
      >
        <div className="px-3 py-2 text-xs uppercase text-cf-textMuted border-b border-cf-border flex items-center justify-between font-medium">
          <span>
            {title} ({items.length})
          </span>
        </div>

        <div>
          {items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <Button
                key={item.to}
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => {
                  setOpen(false);
                  navigate(item.to);
                }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-cf-surfaceActive transition-colors h-auto rounded-none ${
                  item.isActive
                    ? 'text-cf-orange font-medium bg-cf-surfaceActive/60'
                    : 'text-cf-textPrimary'
                }`}
              >
                <div className="flex items-center gap-2 truncate min-w-0">
                  <ItemIcon
                    size={13}
                    className={item.isActive ? 'text-cf-orange flex-shrink-0' : 'text-cf-textMuted flex-shrink-0'}
                  />
                  <span className="truncate">{item.label}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                  {item.badge !== undefined && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-cf-surfaceActive text-cf-textSecondary font-medium">
                      {item.badge}
                    </span>
                  )}
                  {item.isActive && <Check size={12} className="text-cf-orange flex-shrink-0" />}
                </div>
              </Button>
            );
          })}
        </div>
      </PortalPopover>
    </div>
  );
};
